import React, { useEffect, useState } from 'react';
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  RefreshCw,
  Banknote,
  Mail,
  Calendar,
  DollarSign,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

/* ─────────────────────────────────────────────────────────────────────
 * Dashboard → Settings → Payments
 *
 * Single source of truth for ALL payment-provider connections. Today
 * we surface only Stripe; the component is intentionally structured as
 * a stack of <ProviderCard>s so future processors can
 * drop in as siblings later without rewriting the layout.
 *
 * Sensitive financial data (full bank #, SSN, EIN, tax forms) is NEVER
 * stored on NoFee. Owners click "Manage Stripe Account" → land on
 * Stripe's Express Dashboard for everything sensitive.
 * ─────────────────────────────────────────────────────────────────── */

const fmtMoney = (amount, currency) => {
  if (amount === null || amount === undefined) return '—';
  const c = (currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: c,
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const ConnectionBadge = ({ connected, chargesEnabled }) => {
  if (connected && chargesEnabled) {
    return (
      <span
        data-testid="payments-stripe-status-connected"
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold"
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> Connected
      </span>
    );
  }
  if (connected && !chargesEnabled) {
    return (
      <span
        data-testid="payments-stripe-status-pending"
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold"
      >
        <AlertCircle className="w-3.5 h-3.5" /> Onboarding incomplete
      </span>
    );
  }
  return (
    <span
      data-testid="payments-stripe-status-disconnected"
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold"
    >
      <AlertCircle className="w-3.5 h-3.5" /> Not connected
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-3 text-sm text-slate-600">
      <Icon className="w-4 h-4 text-slate-400" />
      <span>{label}</span>
    </div>
    <span
      className="text-sm font-semibold text-slate-900"
      data-testid={testid}
    >
      {value || '—'}
    </span>
  </div>
);

const StripeProviderCard = ({ restaurant }) => {
  const { api } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingDash, setOpeningDash] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (!restaurant?.id) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/restaurants/${restaurant.id}/stripe/account-details`);
        if (cancelled) return;
        setDetails(res.data);
      } catch (err) {
        // Non-connected restaurants get a 200 with connected:false from
        // the backend; reaching this catch means an actual network or
        // permission failure — fall back to the not-connected UI.
        console.error('Stripe account-details fetch failed', err);
        if (cancelled) return;
        setDetails({ connected: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [api, restaurant?.id]);

  const openExpressDashboard = async () => {
    if (!restaurant?.id) return;
    setOpeningDash(true);
    try {
      const res = await api.post(`/restaurants/${restaurant.id}/stripe/login-link`);
      if (res?.data?.url) {
        // Open in a new tab so the owner doesn't lose their NoFee session.
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Could not open Stripe dashboard. Try reconnecting.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Could not open Stripe dashboard');
    } finally {
      setOpeningDash(false);
    }
  };

  const reconnectStripe = async () => {
    if (!restaurant?.id) return;
    setReconnecting(true);
    try {
      const res = await api.post(`/restaurants/${restaurant.id}/stripe/onboard`, {
        origin_url: window.location.origin,
      });
      if (res?.data?.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else {
        toast.error('Could not start Stripe onboarding');
        setReconnecting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Could not start Stripe onboarding');
      setReconnecting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="payments-stripe-loading"
        className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-center"
      >
        <Loader2 className="w-6 h-6 text-gold-500 animate-spin" />
      </div>
    );
  }

  const connected = !!details?.connected;
  const chargesEnabled = !!details?.charges_enabled;
  const bankLast4 = details?.bank_last4 || '';
  const bankName = details?.bank_name || '';
  const email = details?.email || '';
  const lastPayout = details?.last_payout || null;

  return (
    <div
      data-testid="payments-stripe-card"
      className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7"
    >
      {/* Header: provider name + status badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#635BFF] flex items-center justify-center shrink-0">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 leading-tight">Stripe Connection</h3>
            <p className="text-xs text-slate-500">Card payments · Direct payouts to your bank</p>
          </div>
        </div>
        <ConnectionBadge connected={connected} chargesEnabled={chargesEnabled} />
      </div>

      {/* Body switches on connection state */}
      {connected ? (
        <>
          {/* Account info — never expose full bank #, only last-4 */}
          <div
            data-testid="payments-stripe-info"
            className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-1 mb-5"
          >
            <InfoRow
              icon={Banknote}
              label="Bank account"
              value={bankLast4 ? `${bankName ? bankName + ' ' : ''}••••${bankLast4}` : 'Not set in Stripe'}
              testid="payments-stripe-bank-last4"
            />
            <InfoRow
              icon={Mail}
              label="Connected email"
              value={email}
              testid="payments-stripe-email"
            />
            <InfoRow
              icon={Calendar}
              label="Last payout"
              value={lastPayout ? fmtDate(lastPayout.arrival_date) : 'No payouts yet'}
              testid="payments-stripe-last-payout-date"
            />
            <InfoRow
              icon={DollarSign}
              label="Last payout amount"
              value={lastPayout ? fmtMoney(lastPayout.amount, lastPayout.currency) : '—'}
              testid="payments-stripe-last-payout-amount"
            />
          </div>

          {/* Onboarding-incomplete warning */}
          {!chargesEnabled && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-5 flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Onboarding not complete</p>
                <p>Stripe still needs some information before you can accept payments. Click <strong>Reconnect Stripe</strong> below to finish onboarding.</p>
              </div>
            </div>
          )}

          {/* Action buttons — large + mobile friendly. The Stripe Express
              Dashboard login link can ONLY be generated for accounts that
              have completed onboarding (charges_enabled=true). When the
              account is still pending verification we hide the Manage
              button and surface "Continue onboarding" via Reconnect. */}
          <div className="flex flex-col sm:flex-row gap-3">
            {chargesEnabled && (
              <button
                data-testid="payments-stripe-manage-btn"
                onClick={openExpressDashboard}
                disabled={openingDash}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#635BFF] hover:bg-[#5247e8] disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors text-base"
              >
                {openingDash ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</>
                ) : (
                  <>Manage Stripe Account <ExternalLink className="w-4 h-4" /></>
                )}
              </button>
            )}
            <button
              data-testid="payments-stripe-reconnect-btn"
              onClick={reconnectStripe}
              disabled={reconnecting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-white hover:bg-slate-50 disabled:bg-slate-100 text-slate-800 font-semibold rounded-xl border border-slate-300 transition-colors text-base"
            >
              {reconnecting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
              ) : (
                <><RefreshCw className="w-4 h-4" /> {chargesEnabled ? 'Reconnect Stripe' : 'Continue onboarding'}</>
              )}
            </button>
          </div>
        </>
      ) : (
        /* Not-connected state */
        <>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 mb-5">
            <p className="text-sm text-slate-700 mb-2 font-semibold">No Stripe account yet</p>
            <p className="text-sm text-slate-600">
              Connect Stripe (free, ~5 minutes) so customers can pay you by card directly on your menu. Payments go directly to your own Stripe account. NoFee never holds your money.
            </p>
          </div>
          <button
            data-testid="payments-stripe-connect-btn"
            onClick={reconnectStripe}
            disabled={reconnecting}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#635BFF] hover:bg-[#5247e8] disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors text-base"
          >
            {reconnecting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
            ) : (
              <>Connect Stripe <ExternalLink className="w-4 h-4" /></>
            )}
          </button>
        </>
      )}

      {/* Help text — always visible so owners know where sensitive data lives */}
      <div className="mt-5 pt-5 border-t border-slate-100 flex gap-3 items-start">
        <Shield className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500 leading-relaxed">
          Manage your bank account, payouts, business information, and tax settings securely through Stripe. NoFee never stores your bank account number, routing number, SSN, EIN or other sensitive financial information.
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
 * Top-level Payments panel — Stripe is the exclusive provider.
 * ─────────────────────────────────────────────────────────────────── */
const PaymentMethodsPanel = ({ restaurant }) => {
  return (
    <div data-testid="payments-panel" className="space-y-4 max-w-3xl">
      <div className="mb-2">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Payments</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage how you receive money. Add or update bank account, payout info and tax settings inside Stripe.
        </p>
      </div>

      <StripeProviderCard restaurant={restaurant} />
    </div>
  );
};

export default PaymentMethodsPanel;
